import { useMemo } from 'react';
import { DateTime } from 'luxon';
import { CodeMode, ProfessionTypesEnum, SubscriptionStatus } from '@/type';
import type { CustomCodesType } from '@/type';
import { useUserMeQuery, useUserNetworkCodesQuery, useUserSubscriptionQuery } from '@/graphql';
import { LS_KEY_NATIVE_OS_NAME } from '@/utils/constants';
import { useAuth } from '@/contexts/auth-context';

export const useGetPremiumData = () => {
  const { token } = useAuth();
  const { data: userData } = useUserMeQuery({
    skip: !token,
  });
  const { data: codesData } = useUserNetworkCodesQuery({
    skip: !token,
  });
  const { data: subsData } = useUserSubscriptionQuery({
    skip: !token,
  });

  const currentCode = useMemo<CustomCodesType | undefined | null>(
    () =>
      codesData?.codes?.reduce((code, it) => {
        const now = DateTime.local();
        const start = DateTime.fromISO(it?.startDate);
        const finish = DateTime.fromISO(it?.finishDate).endOf('day');
        if (start < now && finish > now) {
          if (code && DateTime.fromISO(code?.finishDate) > finish) return code;
          return it;
        }
        return code;
      }, null),
    [codesData]
  );

  const isUserPremiumByCode = useMemo(
    () => (currentCode ? currentCode?.codeMode?.valueOf() === CodeMode.Premium : null),
    [currentCode]
  );

  const isUserPremiumByPayment = useMemo(
    () =>
      subsData
        ? subsData?.subscription?.status === SubscriptionStatus.ACTIVE ||
        subsData?.subscription?.status === SubscriptionStatus.TRIALING ||
        subsData?.subscription?.status === SubscriptionStatus.PAST_DUE
        : null,
    [subsData]
  );

  const isUserPremiumByFriendship = useMemo(
    () => (subsData ? !!subsData?.me?.isPromotionPremium : null),
    [subsData]
  );
  const isUserInAppPremium = useMemo(
    () => (subsData ? !!subsData?.me?.isInAppPremium : null),
    [subsData]
  );

  const isUserPremium = useMemo(
    () =>
      subsData
        ? !!subsData?.me?.isPremium ||
        isUserInAppPremium ||
        isUserPremiumByCode ||
        isUserPremiumByFriendship
        : null,
    [subsData, isUserInAppPremium, isUserPremiumByCode, isUserPremiumByFriendship]
  );

  const premiumFinishDate = useMemo(() => {
    return (
      subsData?.subscription?.currentPeriodEnd ||
      currentCode?.finishDate ||
      subsData?.me?.promotionFinishDate
    );
  }, [subsData, currentCode]);

  const isPlatformSuitableForSubscription = useMemo(() => {
    const subscriptionOriginStore = subsData?.subscription?.store;
    const nativeOSName = localStorage.getItem(LS_KEY_NATIVE_OS_NAME);
    if (nativeOSName === null) {
      return subscriptionOriginStore === 'STRIPE';
    }
    if (nativeOSName === 'android') {
      return subscriptionOriginStore === 'PLAY_STORE';
    }
    if (nativeOSName === 'ios') {
      return subscriptionOriginStore === 'APP_STORE';
    }
    return false;
  }, [subsData]);

  const isUserStudent = useMemo(() => {
    const userType = userData?.me?.profession?.type?.type;
    if (!userType) return null;
    return (
      userType === ProfessionTypesEnum.Student ||
      userType === ProfessionTypesEnum.Trainee ||
      userType === ProfessionTypesEnum.TraineeToNurse
    );
  }, [userData]);
  const isSpecificProfession = useMemo(() => {
    if (!userData) return null;
    return (
      userData?.me?.speciality?.title === 'Rheumatologie' &&
      userData?.me?.profession?.type?.type === ProfessionTypesEnum.MedicalAssistant
    );
  }, [userData]);

  return {
    isUserPremium,
    isUserPremiumByPayment,
    isUserInAppPremium,
    isUserPremiumByCode,
    isUserPremiumByFriendship,
    premiumFinishDate,
    currentCode,
    isPlatformSuitableForSubscription,
    isUserStudent,
    isSpecificProfession,
  };
};
